/**
 * Catalogo del menu.
 *
 * Trascrive l'albero di `docs/navigazione-e-viste.md` (§2.1) e ricava la
 * visibilita dai permessi, non dai ruoli: la tabella del §2.2 e una
 * conseguenza della matrice di `@calcio/shared`, e riscriverla qui la
 * farebbe divergere al primo ritocco. Il test la verifica voce per voce.
 *
 * Le voci delle viste non ancora realizzate restano nel catalogo con
 * `pronta: false` e non compaiono: il menu e filtrato, non disabilitato.
 */

import type { Assegnazione, Permesso } from '@calcio/shared';
import { AMBITO_DEL_RUOLO, ambitoDelPermesso, puo } from '@calcio/shared';
import {
  ActivityIcon,
  Building2Icon,
  BuildingIcon,
  CalendarRangeIcon,
  ChartColumnIcon,
  ClipboardListIcon,
  ContactIcon,
  DumbbellIcon,
  GaugeIcon,
  GitCompareArrowsIcon,
  GoalIcon,
  HistoryIcon,
  HouseIcon,
  KeyRoundIcon,
  LayersIcon,
  ListChecksIcon,
  MessagesSquareIcon,
  RulerIcon,
  SettingsIcon,
  ShieldIcon,
  TrophyIcon,
  UserCogIcon,
  UserRoundIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react';

export interface Voce {
  chiave: string;
  etichetta: string;
  /** Accanto all'etichetta nel menu. */
  icona: LucideIcon;
  /** Di squadra-stagione, di societa o di piattaforma: decide come si verifica il permesso. */
  ambito: 'squadra' | 'societa' | 'piattaforma';
  permesso: Permesso;
  /** Le voci di squadra ricevono la squadra-stagione corrente, le altre la ignorano. */
  percorso: (ss: string) => string;
  gruppo?: string;
  /** Posizione nella barra inferiore di Mister e Dirigente su mobile. */
  barra?: number;
  /**
   * Nel catalogo ma non nel menu disegnato: l'assistente e un pannello e non
   * una vista (§1.5), e il suo storico si apre dalla sezione Assistente IA
   * che disegna il guscio. La voce serve comunque al guscio, che da li ricava
   * titolo e permesso.
   */
  fuoriMenu?: boolean;
  pronta: boolean;
}

export interface Sezione {
  titolo: string | null;
  voci: Voce[];
}

export interface Chi {
  superAdmin: boolean;
  assegnazioni: readonly Assegnazione[];
}

/**
 * L'assistente occupa la quinta posizione della barra inferiore ma non e
 * una voce: e un pannello che si apre sopra la vista (§1.5). Nel catalogo
 * c'e il solo storico delle conversazioni (V59), che e una vista vera ma si
 * raggiunge dalla sezione Assistente IA che disegna il guscio.
 */
export const CATALOGO: readonly Sezione[] = [
  {
    titolo: null,
    voci: [
      {
        chiave: 'cruscotto',
        etichetta: 'Home',
        icona: HouseIcon,
        ambito: 'squadra',
        permesso: 'partita.leggi',
        percorso: (ss) => `/sq/${ss}`,
        barra: 1,
        pronta: true,
      },
    ],
  },
  {
    titolo: 'Squadra',
    voci: [
      {
        chiave: 'rosa',
        etichetta: 'Rosa',
        icona: UsersIcon,
        ambito: 'squadra',
        permesso: 'giocatore.leggi',
        percorso: (ss) => `/sq/${ss}/rosa`,
        barra: 2,
        pronta: true,
      },
      {
        chiave: 'allenamenti',
        etichetta: 'Allenamenti',
        icona: DumbbellIcon,
        ambito: 'squadra',
        permesso: 'allenamento.leggi',
        percorso: (ss) => `/sq/${ss}/allenamenti`,
        barra: 3,
        pronta: true,
      },
      {
        chiave: 'partite',
        etichetta: 'Partite',
        icona: GoalIcon,
        ambito: 'squadra',
        permesso: 'partita.leggi',
        percorso: (ss) => `/sq/${ss}/partite`,
        barra: 4,
        pronta: true,
      },
      {
        chiave: 'carichi',
        etichetta: 'Carichi',
        icona: ActivityIcon,
        ambito: 'squadra',
        permesso: 'allenamento.leggi',
        percorso: (ss) => `/sq/${ss}/carichi`,
        pronta: true,
      },
      {
        chiave: 'statistiche',
        etichetta: 'Statistiche',
        icona: ChartColumnIcon,
        ambito: 'squadra',
        permesso: 'partita.leggi',
        percorso: (ss) => `/sq/${ss}/statistiche`,
        pronta: true,
      },
      {
        chiave: 'schede',
        etichetta: 'Libreria schede',
        icona: ClipboardListIcon,
        ambito: 'squadra',
        permesso: 'allenamento.leggi',
        percorso: (ss) => `/sq/${ss}/schede`,
        pronta: true,
      },
      {
        chiave: 'attributi',
        etichetta: 'Attributi',
        icona: GaugeIcon,
        ambito: 'squadra',
        permesso: 'misurazione.leggi',
        percorso: (ss) => `/sq/${ss}/valutazioni/attributi`,
        gruppo: 'Valutazioni',
        pronta: true,
      },
      {
        chiave: 'test',
        etichetta: 'Test',
        icona: RulerIcon,
        ambito: 'squadra',
        permesso: 'misurazione.leggi',
        percorso: (ss) => `/sq/${ss}/test`,
        gruppo: 'Valutazioni',
        pronta: true,
      },
      {
        chiave: 'confronto',
        etichetta: 'Confronto',
        icona: GitCompareArrowsIcon,
        ambito: 'squadra',
        permesso: 'misurazione.leggi',
        percorso: (ss) => `/sq/${ss}/valutazioni/confronto`,
        gruppo: 'Valutazioni',
        pronta: true,
      },
      {
        chiave: 'impostazioni',
        etichetta: 'Impostazioni squadra',
        icona: SettingsIcon,
        ambito: 'squadra',
        permesso: 'societa.squadre.gestisci',
        percorso: (ss) => `/sq/${ss}/impostazioni`,
        pronta: true,
      },
      {
        chiave: 'staff',
        etichetta: 'Staff',
        icona: UserCogIcon,
        ambito: 'squadra',
        permesso: 'societa.utenze.gestisci',
        percorso: (ss) => `/sq/${ss}/staff`,
        pronta: true,
      },
    ],
  },
  {
    titolo: 'Società',
    voci: [
      {
        chiave: 'giocatori',
        etichetta: 'Giocatori',
        icona: ContactIcon,
        ambito: 'societa',
        permesso: 'giocatore.leggi',
        percorso: () => '/giocatori',
        pronta: true,
      },
      {
        chiave: 'competizioni',
        etichetta: 'Competizioni',
        icona: TrophyIcon,
        ambito: 'societa',
        permesso: 'competizione.gestisci',
        percorso: () => '/competizioni',
        pronta: true,
      },
    ],
  },
  {
    titolo: 'Amministrazione',
    voci: [
      {
        chiave: 'dati-societa',
        etichetta: 'Dati società',
        icona: BuildingIcon,
        ambito: 'societa',
        permesso: 'societa.dati.gestisci',
        percorso: () => '/amministrazione/societa',
        pronta: true,
      },
      {
        chiave: 'stagioni',
        etichetta: 'Stagioni',
        icona: CalendarRangeIcon,
        ambito: 'societa',
        permesso: 'societa.stagioni.gestisci',
        percorso: () => '/amministrazione/stagioni',
        pronta: true,
      },
      {
        chiave: 'squadre',
        etichetta: 'Squadre',
        icona: ShieldIcon,
        ambito: 'societa',
        permesso: 'societa.squadre.gestisci',
        percorso: () => '/amministrazione/squadre',
        pronta: true,
      },
      {
        chiave: 'squadre-stagione',
        etichetta: 'Squadre-stagione',
        icona: LayersIcon,
        ambito: 'societa',
        permesso: 'societa.squadre.gestisci',
        percorso: () => '/amministrazione/squadre-stagione',
        pronta: true,
      },
      {
        chiave: 'account',
        etichetta: 'Account e ruoli',
        icona: KeyRoundIcon,
        ambito: 'societa',
        permesso: 'societa.utenze.gestisci',
        percorso: () => '/amministrazione/account',
        pronta: true,
      },
      {
        chiave: 'catalogo-attributi',
        etichetta: 'Catalogo attributi',
        icona: ListChecksIcon,
        ambito: 'societa',
        permesso: 'attributo.catalogo.gestisci',
        percorso: () => '/amministrazione/attributi',
        pronta: true,
      },
      {
        chiave: 'registro',
        etichetta: 'Registro attività',
        icona: HistoryIcon,
        ambito: 'societa',
        permesso: 'societa.dati.gestisci',
        percorso: () => '/amministrazione/registro',
        pronta: true,
      },
    ],
  },
  {
    titolo: 'Assistente IA',
    voci: [
      {
        chiave: 'conversazioni',
        etichetta: 'Conversazioni',
        icona: MessagesSquareIcon,
        ambito: 'societa',
        permesso: 'assistente.usa',
        percorso: () => '/assistente',
        fuoriMenu: true,
        pronta: true,
      },
    ],
  },
  {
    titolo: 'Piattaforma',
    voci: [
      {
        chiave: 'piattaforma-societa',
        etichetta: 'Società',
        icona: Building2Icon,
        ambito: 'piattaforma',
        permesso: 'piattaforma.societa.gestisci',
        percorso: () => '/piattaforma/societa',
        pronta: true,
      },
      {
        chiave: 'piattaforma-account',
        etichetta: 'Account',
        icona: UserRoundIcon,
        ambito: 'piattaforma',
        permesso: 'piattaforma.societa.gestisci',
        percorso: () => '/piattaforma/account',
        pronta: true,
      },
      {
        chiave: 'piattaforma-registro',
        etichetta: 'Registro attività',
        icona: HistoryIcon,
        ambito: 'piattaforma',
        permesso: 'piattaforma.societa.gestisci',
        percorso: () => '/piattaforma/registro',
        pronta: true,
      },
    ],
  },
];

/** La voce del catalogo con questa chiave: una chiave sbagliata e un errore di programma. */
export function voce(chiave: string): Voce {
  const trovata = CATALOGO.flatMap((s) => s.voci).find((v) => v.chiave === chiave);
  if (!trovata) throw new Error(`Voce di menu inesistente: ${chiave}`);
  return trovata;
}

/**
 * Le voci di squadra valgono sulla squadra-stagione aperta. Per quelle di
 * societa basta un'assegnazione qualunque: il Mister apre i giocatori delle
 * sue rose anche da un'altra squadra.
 * Il Super Admin vede la piattaforma per il suo flag, non per un'assegnazione.
 */
export function visibile(voce: Voce, chi: Chi, ss?: string): boolean {
  switch (voce.ambito) {
    case 'piattaforma':
      return chi.superAdmin;
    case 'societa': {
      const ambito = ambitoDelPermesso(chi.assegnazioni, voce.permesso);
      return ambito === 'societa' || ambito.length > 0;
    }
    case 'squadra':
      return ss !== undefined && puo(chi.assegnazioni, voce.permesso, ss);
  }
}

/**
 * Le voci di societa stanno nel menu di chi ha il permesso sulla societa
 * intera. Mister e Dirigente, con un permesso su qualche squadra, aprono la
 * scheda giocatore dalla rosa e dal cruscotto, ma non la cercano nel menu: i
 * loro giocatori sono quelli della rosa. Le competizioni non le aprono affatto,
 * perche le gestisce l'Admin Squadra.
 */
function nelMenu(voce: Voce, chi: Chi, ss?: string): boolean {
  if (voce.fuoriMenu || !visibile(voce, chi, ss)) return false;
  return (
    voce.ambito !== 'societa' || ambitoDelPermesso(chi.assegnazioni, voce.permesso) === 'societa'
  );
}

/** Le voci del menu visibili a questo utente: quelle fuori menu non lo sono. */
export function vociVisibili(chi: Chi, ss?: string): Voce[] {
  return CATALOGO.flatMap((s) => s.voci).filter((v) => nelMenu(v, chi, ss));
}

/** Il menu da disegnare: voci pronte, visibili e nel menu; sezioni vuote tolte. */
export function menu(chi: Chi, ss?: string): Sezione[] {
  return CATALOGO.map((s) => ({
    titolo: s.titolo,
    voci: s.voci.filter((v) => v.pronta && nelMenu(v, chi, ss)),
  })).filter((s) => s.voci.length > 0);
}

/**
 * La vista di societa o di piattaforma aperta a un percorso, se pronta e
 * visibile. Serve ai gusci fuori dal prefisso di squadra, che non hanno un
 * `ss` da controllare: vince la radice piu lunga, perche `/squadre` e
 * prefisso di `/squadre-stagione` solo come stringa.
 */
export function voceAperta(percorso: string, chi: Chi): Voce | undefined {
  const [voce] = CATALOGO.flatMap((s) => s.voci)
    .filter((v) => v.ambito !== 'squadra')
    .filter((v) => percorso === v.percorso('') || percorso.startsWith(`${v.percorso('')}/`))
    .sort((x, y) => y.percorso('').length - x.percorso('').length);
  return voce?.pronta && visibile(voce, chi) ? voce : undefined;
}

/** Titolo della sezione del menu a cui appartiene la voce. */
export function sezioneDi(voce: Voce): string | null {
  return CATALOGO.find((s) => s.voci.includes(voce))?.titolo ?? null;
}

/**
 * La barra inferiore e di Mister e Dirigente, che aprono tre sezioni col
 * pollice a bordo campo (§1.4). Chi ha un ruolo di societa o di piattaforma
 * lavora da scrivania e ha la barra laterale anche su mobile.
 */
export function usaBarraInferiore(chi: Chi): boolean {
  return (
    !chi.superAdmin &&
    chi.assegnazioni.length > 0 &&
    chi.assegnazioni.every((a) => AMBITO_DEL_RUOLO[a.ruolo] === 'squadra_stagione')
  );
}

export function barraInferiore(chi: Chi, ss: string): Voce[] {
  if (!usaBarraInferiore(chi)) return [];
  return vociVisibili(chi, ss)
    .filter((v) => v.pronta && v.barra !== undefined)
    .sort((a, b) => a.barra! - b.barra!);
}

/**
 * Percorso equivalente su un'altra squadra-stagione. Si tiene la sezione e
 * non il dettaglio: `/sq/A/partite/123` porta a `/sq/B/partite`, perche la
 * partita 123 e della squadra A e sulla B non esiste.
 */
export function cambiaSquadra(percorso: string, da: string, a: string): string {
  const [voce] = CATALOGO.flatMap((s) => s.voci)
    .filter((v) => v.ambito === 'squadra')
    .map((v) => ({ v, radice: v.percorso(da) }))
    .filter(({ radice }) => percorso === radice || percorso.startsWith(`${radice}/`))
    .sort((x, y) => y.radice.length - x.radice.length);
  return voce ? voce.v.percorso(a) : `/sq/${a}`;
}
